"use client"

import { useEffect, useRef } from "react"
import type { DataImportStatistics } from "@/schema/data-import"
import { Button } from "@ui/shad"
import { Loader2 } from "lucide-react"

import { useFetchDataImportProgress } from "@/hooks/api/data-imports"

interface ImportProgressProps {
  dataImportId: string
  onComplete: (statistics: DataImportStatistics) => void
  onClose: () => void
  autoCloseOnImporting?: boolean
  onImportStarted?: () => void
}

export function ImportProgress({
  dataImportId,
  onComplete,
  onClose,
  autoCloseOnImporting,
  onImportStarted,
}: ImportProgressProps) {
  const { data, isLoading } = useFetchDataImportProgress(dataImportId, {
    refetchInterval: 1000,
  })

  useEffect(() => {
    if (autoCloseOnImporting && !isLoading && data?.status === "importing") {
      onImportStarted?.()
      onClose()
    }
  }, [autoCloseOnImporting, isLoading, data?.status, onClose, onImportStarted])

  // Track max percentage to prevent progress bar from going backwards
  const maxPercentageRef = useRef<number>(0)

  const rawPercentage = data?.progress_percentage
  if (rawPercentage !== null && rawPercentage !== undefined && rawPercentage > maxPercentageRef.current) {
    maxPercentageRef.current = rawPercentage
  }

  const progressPercentage = maxPercentageRef.current > 0 ? maxPercentageRef.current : rawPercentage
  const stats = data?.statistics ?? {}
  const isPreparing = isLoading || rawPercentage === null

  const importedCount = (stats.imported_households ?? 0) + (stats.imported_businesses ?? 0)
  const totalCount = (stats.total_households ?? 0) + (stats.total_businesses ?? 0)

  // Auto-advance when import completes (either by status or when all records are imported)
  useEffect(() => {
    const allRecordsImported = totalCount > 0 && importedCount >= totalCount
    if (data?.status === "completed" || allRecordsImported) {
      onComplete(stats)
    }
  }, [data?.status, importedCount, totalCount, onComplete, stats])

  return (
    <div className="mx-auto flex max-w-xl flex-col items-center gap-6 py-12">
      <Loader2 className="text-muted-foreground h-16 w-16 animate-spin" />

      <div className="text-center">
        <h3 className="text-lg font-medium">Importing your data</h3>
        <p className="text-muted-foreground mt-1">{isPreparing ? "Preparing..." : `${progressPercentage}% complete`}</p>
      </div>

      <div className="bg-muted relative h-2 w-80 overflow-hidden rounded-full">
        <div
          className="bg-primary h-full transition-all duration-300 ease-out"
          style={{ width: `${isPreparing ? 0 : progressPercentage}%` }}
        />
      </div>

      {totalCount > 0 && (
        <p className="text-muted-foreground text-sm">
          Imported {importedCount} of {totalCount} records
        </p>
      )}

      {!autoCloseOnImporting && (
        <div className="text-center">
          <p className="text-muted-foreground text-xs">You can close this dialog and continue using Slant.</p>
          <p className="text-muted-foreground text-xs">Your data will continue importing in the background.</p>
          <Button variant="ghost" size="sm" onClick={onClose} className="mt-4">
            Close and continue
          </Button>
        </div>
      )}
    </div>
  )
}
